import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import useSpotConfig from '@host-panel/request/config/use-config';

import RadioButtonPanel from '../../../common/inputs/radio-button-panel';
import CheckboxInput from '../../../common/inputs/checkbox-input';
import NumericInput from '../../../common/inputs/customs/num-input-add';
import { useSendEvent } from '../../../../tracking/useSendEvent';
import { createSettingsChangedPayload } from '../settings-send-event-utils';
import { showToast } from '../../../../../components/toast/toastSlice';

import { AnalyticsProvider } from 'app/scripts/services/analytics-provider';
import { useTypedSelector } from 'state';

// Selection options
const REALTIME_MODERATION = 'REALTIME_MODERATION';
const SHADOW_MODE = 'SHADOW_MODE';

// Config feature flags
const REALTIME_FF = 'tenant.conversation.realtimeModerationEnabled';
const AFTERCLICK_FF = 'tenant.conversation.afterclickModerationEnabled';
const SUSPENSION_FF = 'tenant.moderation.suspensions_enabled';
const IS_APPEAL_ENABLED_FF = 'tenant.conversation.isAppealEnabled';
const MONTHLY_APPEALS_LIMIT = 'tenant.conversation.monthlyAppealsLimit';

const DEFAULT_MONTHLY_APPEALS_LIMIT = 15;

type Props = {
  isSuperAdmin: boolean;
  isShadowMode: boolean;
  handleShadowMode: (boolean) => void;
};

const InConversationModeration: React.FC<Props> = ({
  isSuperAdmin,
  isShadowMode,
  handleShadowMode
}) => {
  const [configValues, setConfig] = useSpotConfig(
    [REALTIME_FF, AFTERCLICK_FF, SUSPENSION_FF, IS_APPEAL_ENABLED_FF, MONTHLY_APPEALS_LIMIT],
    {
      [REALTIME_FF]: false,
      [AFTERCLICK_FF]: false,
      [SUSPENSION_FF]: false,
      [IS_APPEAL_ENABLED_FF]: false,
      [MONTHLY_APPEALS_LIMIT]: DEFAULT_MONTHLY_APPEALS_LIMIT
    }
  );
  const [expandedOption, setExpandedOption] = useState<string>('NONE');
  const [monthlyAppealsLimitCounter, setMonthlyAppealsLimitCounter] = useState(
    DEFAULT_MONTHLY_APPEALS_LIMIT
  );

  const { sendEvent } = useSendEvent();
  const dispatch = useDispatch();

  const currentUser = useTypedSelector(state => state.currentUser);

  const [
    isRealtimeEnabled,
    isAfterClickEnabled,
    isSuspensionsEnabled,
    isAppealEnabled,
    monthlyAppealsLimit
  ] = configValues;

  useEffect(() => {
    setMonthlyAppealsLimitCounter(monthlyAppealsLimit as number);
  }, [monthlyAppealsLimit]);

  const setShadowMode = (value: boolean) => {
    if (isShadowMode !== value) {
      handleShadowMode(value);
      AnalyticsProvider.track(value ? 'Shadow mode clicked' : 'Clarity mode clicked');
    }
  };

  const onRealtimeChange = (value: boolean) => {
    setConfig({ key: REALTIME_FF, value }, undefined, be_success => {
      sendEvent(createSettingsChangedPayload('Real-time feedback img url', value, { be_success }));
    });
    AnalyticsProvider.track('Clarity mode real time clicked', { checked: value });
  };

  const onAfterClickChange = (value: boolean) => {
    setConfig({ key: AFTERCLICK_FF, value }, undefined, be_success => {
      sendEvent(createSettingsChangedPayload('Real-time feedback', value, { be_success }));
    });
    AnalyticsProvider.track('Clarity mode after submission clicked', { checked: value });
  };

  const onSuspensionsChange = (value: boolean) => {
    setConfig({ key: SUSPENSION_FF, value }, undefined, be_success => {
      sendEvent(createSettingsChangedPayload('Auto suspension', value, { be_success }));
    });
    AnalyticsProvider.track('Clarity mode suspension clicked', { checked: value });
  };

  const onAppealEnabledChange = (value: boolean) => {
    setConfig(
      [
        { key: IS_APPEAL_ENABLED_FF, value },
        { key: MONTHLY_APPEALS_LIMIT, value: monthlyAppealsLimit }
      ],
      undefined,
      be_success => {
        sendEvent(createSettingsChangedPayload('Appeal', value, { be_success }));
      }
    );

    AnalyticsProvider.track('Clarity mode appeal clicked', { checked: value });
  };

  const onSaveMonthlyAppealsLimit = (value: string) => {
    setConfig({ key: MONTHLY_APPEALS_LIMIT, value: parseInt(value, 10) }, undefined, isSuccess => {
      if (isSuccess) {
        dispatch(showToast({ type: 'success', message: 'Changes saved' }));
      } else {
        dispatch(
          showToast({
            type: 'error',
            message: 'Failed to save changes. Please try again.'
          })
        );
      }
    });
  };

  return (
    <div className="field">
      <div className="field-title">Moderation User Experience</div>
      <RadioButtonPanel
        value={SHADOW_MODE}
        withExpand={false}
        checked={isShadowMode}
        updateExpend={value => {
          setExpandedOption(value ? SHADOW_MODE : 'NONE');
        }}
        isExpend={expandedOption === SHADOW_MODE}
        onChange={() => setShadowMode(true)}
        className="panel-body-column"
        name="moderation-user-experience"
        title={
          <span>
            <em>Shadow mode</em> - Enable Shadow to hide pending or rejected comment status from the
            commenter.
          </span>
        }
      />
      <RadioButtonPanel
        value={REALTIME_MODERATION}
        checked={!isShadowMode}
        updateExpend={value => {
          setExpandedOption(value ? REALTIME_MODERATION : 'NONE');
        }}
        isExpend={expandedOption === REALTIME_MODERATION}
        onChange={() => setShadowMode(false)}
        className="panel-body-column inner-space"
        name="moderation-user-experience"
        title={
          <span>
            <em>Clarity Mode</em> - Enable clarity mode to show the commenter his comment status in
            the conversation and notify him once his comment status has been updated.
          </span>
        }
      >
        <CheckboxInput
          value={isAfterClickEnabled}
          onChange={onAfterClickChange}
          className="narrow"
          gapTopSize="sm"
          onlyCheckBoxClick={true}
          text={
            <>
              <em>Real-time Feedback</em> - Provide real-time feedback after clicking the submit
              button if the comment is going to be rejected or requires approval.
            </>
          }
        />
        <CheckboxInput
          value={isRealtimeEnabled}
          onChange={onRealtimeChange}
          className="narrow"
          gapTopSize="sm"
          onlyCheckBoxClick={true}
          text={
            <>
              <em>Real-time Feedback (images and link)</em> - Provide feedback while the user is
              typing if the comment contains images or external links if your policy does not allow
              it.
            </>
          }
        />
        <CheckboxInput
          value={isSuspensionsEnabled}
          onChange={onSuspensionsChange}
          className="narrow"
          gapTopSize="sm"
          onlyCheckBoxClick={true}
          text={
            <>
              <em>Auto Suspension</em> - Suspend the commenter from a particular conversation if he
              violates the site guidelines more than 3 times.
            </>
          }
        />
        {(currentUser.isAdmin || currentUser.isSuperAdmin) && (
          <>
            <CheckboxInput
              value={isAppealEnabled}
              onChange={onAppealEnabledChange}
              className="narrow"
              gapTopSize="sm"
              onlyCheckBoxClick={true}
              text={
                <>
                  <em>Appeal</em> - Allow users whose comment were rejected to file an appeal and
                  try to have it reinstated. There is a monthly appeals limit per user to avoid
                  false appeals.
                </>
              }
            />
            <NumericInput
              onClick={onSaveMonthlyAppealsLimit}
              onChange={value => setMonthlyAppealsLimitCounter(value)}
              value={monthlyAppealsLimitCounter}
              gapTopSize="sm"
              disabled={!isAppealEnabled}
              disabledAddButton={!isAppealEnabled}
              status={'success'}
            />
          </>
        )}
      </RadioButtonPanel>
    </div>
  );
};

export default InConversationModeration;
